import { Schema } from './api-spec.interface';

/**
 * Get data type string from schema
 * @param schema - The schema object
 * @returns Data type string
 */
export function getSchemaDataType(schema: Schema | undefined): string {
  if (!schema) return 'unknown';

  if (schema.type === 'array' && schema.items) {
    const itemType = getSchemaDataType(schema.items);
    return `array<${itemType}>`;
  }

  if (schema.enum) {
    return 'enum';
  }

  // Handle oneOf with single schema (enum or wrapped object)
  if (schema.oneOf && schema.oneOf.length === 1) {
    const singleSchema = schema.oneOf[0];
    if (isEnumSchema(singleSchema)) {
      return 'enum';
    }
    return getSchemaDataType(singleSchema);
  }

  if (schema.type) {
    if (schema.format) {
      return `${schema.type} (${schema.format})`;
    }
    return schema.type;
  }

  if (schema.allOf) {
    return 'object (composed)';
  }

  if (schema.oneOf) {
    return 'object (oneOf)';
  }

  return 'object';
}

/**
 * Generate example value from schema
 * @param schema - The schema definition
 * @param fieldName - Optional field name for context
 * @returns Generated example value
 */
export function generateSchemaExample(schema: Schema | undefined, fieldName?: string): unknown {
  if (!schema) return null;

  // Use default value if available
  if (schema.default !== undefined) {
    return schema.default;
  }

  // Use example if provided
  if (schema.example !== undefined) {
    return schema.example;
  }

  // Handle oneOf with single enum or wrapped schema
  if (schema.oneOf && schema.oneOf.length === 1) {
    const singleSchema = schema.oneOf[0];
    if (singleSchema.enum && singleSchema.enum.length > 0) {
      return singleSchema.enum[0];
    }
    return generateSchemaExample(singleSchema, fieldName);
  }

  // Handle enums
  if (schema.enum && schema.enum.length > 0) {
    return schema.enum[0];
  }

  // Generate based on type
  switch (schema.type) {
    case 'string':
      return generateStringExample(schema, fieldName);
    case 'integer':
      return generateIntegerExample(schema, fieldName);
    case 'number':
      return generateNumberExample(schema, fieldName);
    case 'boolean':
      return fieldName?.toLowerCase().includes('false') ? false : true;
    case 'array':
      if (schema.items) {
        const itemExample = generateSchemaExample(schema.items);
        return itemExample !== null ? [itemExample] : [];
      }
      return [];
    case 'object':
      if (schema.properties) {
        const obj: Record<string, unknown> = {};
        Object.entries(schema.properties).forEach(([key, propSchema]: [string, Schema]) => {
          const propExample = generateSchemaExample(propSchema, key);
          if (propExample !== null) {
            obj[key] = propExample;
          }
        });
        return obj;
      }
      return {};
    default:
      return null;
  }
}

/**
 * Generate string example based on format and field name
 * @param schema - The string schema
 * @param fieldName - Optional field name for context
 * @returns Generated string example
 */
export function generateStringExample(schema: Schema, fieldName?: string): string {
  // Format-based examples
  switch (schema.format) {
    case 'date':
      return '2024-01-01';
    case 'date-time':
      return '2024-01-01T12:00:00Z';
    case 'email':
      return 'user@example.com';
    case 'uri':
    case 'url':
      return 'https://example.com';
    case 'uuid':
      return '550e8400-e29b-41d4-a716-446655440000';
    case 'binary':
      return 'base64-encoded-data';
  }

  // Context-based examples
  if (fieldName) {
    const lowerFieldName = fieldName.toLowerCase();

    if (lowerFieldName === 'toformat' || lowerFieldName.includes('format')) return 'png';
    if (lowerFieldName.includes('color')) return '#ffffff';
    if (lowerFieldName.includes('backgroundcolor')) return '#ffffff';
    if (lowerFieldName.includes('imagebase64files')) return 'base64-image-data';
    if (lowerFieldName.includes('id')) return 'example-id-123';
  }

  return 'string';
}

export function isEnumSchema(schema: Schema | undefined): boolean {
  return !!(schema && Array.isArray(schema.enum) && schema.enum.length > 0);
}

export function isSingleEnumOneOf(schema: Schema | undefined): boolean {
  if (!schema?.oneOf || !Array.isArray(schema.oneOf) || schema.oneOf.length !== 1) {
    return false;
  }
  return isEnumSchema(schema.oneOf[0]);
}

export function extractEnumSchema(schema: Schema | undefined): Schema | null {
  if (!schema) {
    return null;
  }

  if (isEnumSchema(schema)) {
    return schema;
  }

  if (isSingleEnumOneOf(schema)) {
    return schema.oneOf?.[0] ?? null;
  }

  return null;
}

export function unwrapSingleOneOf(schema: Schema): Schema {
  if (!schema || !Array.isArray(schema.oneOf) || schema.oneOf.length !== 1 || isSingleEnumOneOf(schema)) {
    return schema;
  }

  const nested = schema.oneOf[0];
  return {
    ...nested,
    nullable: schema.nullable ?? nested.nullable,
    description: schema.description ?? nested.description,
    default: schema.default ?? nested.default,
  };
}

/**
 * Generate integer example
 * @param schema - The integer schema
 * @param fieldName - Optional field name for context
 * @returns Generated integer example
 */
export function generateIntegerExample(schema: Schema, fieldName?: string): number {
  if (schema.minimum !== undefined) {
    return schema.minimum;
  }

  if (fieldName) {
    const lowerFieldName = fieldName.toLowerCase();

    if (lowerFieldName.includes('width')) return 800;
    if (lowerFieldName.includes('height')) return 600;
    if (lowerFieldName.includes('page')) return 0;
    if (lowerFieldName.includes('index')) return 0;
    if (lowerFieldName.includes('top')) return 10;
    if (lowerFieldName.includes('left')) return 10;
    if (lowerFieldName.includes('right')) return 10;
    if (lowerFieldName.includes('bottom')) return 10;
    if (lowerFieldName.includes('x')) return 0;
    if (lowerFieldName.includes('y')) return 0;
    if (lowerFieldName.includes('radius')) return 5;
  }

  return 0;
}

/**
 * Generate number example
 * @param schema - The number schema
 * @param fieldName - Optional field name for context
 * @returns Generated number example
 */
export function generateNumberExample(schema: Schema, fieldName?: string): number {
  if (schema.minimum !== undefined) {
    return schema.minimum;
  }

  if (fieldName) {
    const lowerFieldName = fieldName.toLowerCase();

    if (lowerFieldName.includes('percentage')) return 100.0;
    if (lowerFieldName.includes('angle')) return 0.0;
  }

  return 0.0;
}
